import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { peekScroll } from "@/lib/scrollMemory";


const resetAllScrollContainers = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  // Some pages (e.g. homepage `<main className="h-screen overflow-y-auto">`)
  // use an inner scroll container instead of the document, so resetting
  // window alone leaves them at the previous scroll position.
  document
    .querySelectorAll<HTMLElement>("main, [data-scroll-container]")
    .forEach((el) => {
      if (el.scrollTop !== 0) el.scrollTop = 0;
      if (el.scrollLeft !== 0) el.scrollLeft = 0;
    });
};

const ScrollToTop = () => {
  const { pathname, hash, search } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"

  // Disable the browser's automatic scroll restoration so back/forward
  // navigations don't fight our explicit scroll-to-top.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Reset scroll BEFORE paint on every pathname change (no hash).
  // useLayoutEffect avoids the flash of the previous scroll position when
  // navigating between project detail pages. Runs for PUSH, REPLACE and POP
  // (back/forward) — the user always lands at the top of a new route.
  useLayoutEffect(() => {
    if (hash) return;
    // If the destination page has a saved scroll position (e.g. user is
    // returning to /work from a project detail), skip the reset and let the
    // page restore its own position.
    if (peekScroll(pathname + search) !== null) return;
    resetAllScrollContainers();
    // Re-assert on next frame (covers framer-motion / hero images).
    const raf1 = requestAnimationFrame(() => {
      resetAllScrollContainers();
      // And again the frame after — catches sections that mount in the same
      // microtask as the first rAF (e.g. Suspense fallbacks resolving).
      requestAnimationFrame(resetAllScrollContainers);
    });
    // Backup resets after lazy chunks / Suspense boundaries / data-loading
    // states (e.g. ProjectDetail's `loading=true → false` transition). Two
    // timeouts give slow connections a wider window to settle.
    const t1 = window.setTimeout(resetAllScrollContainers, 120);
    const t2 = window.setTimeout(resetAllScrollContainers, 360);
    return () => {
      cancelAnimationFrame(raf1);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pathname, hash, navType]);

  // Hash navigation: poll until the target stabilizes (covers lazy chunks
  // and Suspense data loads), then a single smooth scroll with the
  // 88px sticky-header offset already used site-wide.
  useEffect(() => {
    if (!hash) return;
    const normalizedHash = decodeURIComponent(hash);
    let attempts = 0;
    const maxAttempts = 40;

    const tick = async () => {
      attempts += 1;
      const el = document.querySelector(normalizedHash);
      if (el) {
        const { scrollToHash } = await import("@/lib/smoothScroll");
        // First settle layout, then one smooth scroll.
        scrollToHash(normalizedHash, { smooth: false });
        requestAnimationFrame(() => scrollToHash(normalizedHash));
        return;
      }
      if (attempts < maxAttempts) {
        window.setTimeout(tick, 120);
      }
    };

    const t = window.setTimeout(tick, 80);
    return () => window.clearTimeout(t);
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
